// ─────────────────────────────────────────────────────────────────────────────
// buckLane.ts — THE LIVE STRIP, AND WHAT ARMING BUCK ACTUALLY MEANS.
//
// Pure. No React, no fetch. Every state the strip can be in is reachable from a
// test without a running walk — which matters more here than anywhere else in
// this surface, because the states that carry the value are the ones a browser
// will almost never be pointed at: the tick that has not landed, the build that
// publishes no round detail, the walk that has banked nothing yet.
//
// ── THE OWNER SHOULD NOT NEED `journalctl` TO SEE HIS OWN AGENT ────────────
// The log says `[GROWTH/RUNNER] Buck is WORKING — walk started (supervisor)`.
// The surface says the same thing, in his words, where he already is.
//
// ── ONLY BUCK, AND THAT IS HONEST RATHER THAN HIDDEN ───────────────────────
// The other nine agents have no work harness. A general "start the agent" strip
// would be a promise nine of them cannot keep, so it renders for Buck and not
// for them — the same narrowness the backend route already chose for the kick
// ("a general branch here would be a promise nine of them cannot keep").
//
// ── AND IT NEVER IMPLIES AN INSTANT HALT ───────────────────────────────────
// OWNER RULING: BUCK OFF MEANS HE FINISHES THE ROUND IN FLIGHT AND STOPS — up to
// about three and a half minutes — NEVER KILLED MID-COMPANY. The backend agrees
// by construction: the stop is `growth_harness.run` re-reading consent at the
// top of every round, and app.py's disarm branch deliberately makes no kill
// call. A control that implied an instant stop would be describing a mechanism
// that does not exist.
// ─────────────────────────────────────────────────────────────────────────────

import { RunnerStatus, RunnerLive, RunnerLast } from '../api/growthRunner';
import { STOPPING_LINE } from './runControl';

/**
 * THE ONE DEFINITION of "this is Buck's chat". Takes the PAIR, never the agent
 * alone: deriving the domain from the agent is `project_domain_agent_pair_drift`
 * exactly, and this is the guard that decides whether nine other agents get a
 * strip they cannot honour.
 *
 * It matches the backend's own key normalisation — domain uppercased, agent
 * lowercased — so BUCK, Buck and buck are one agent here as they are there.
 */
export function isBuckPair(domain: unknown, agent: unknown): boolean {
  return String(domain ?? '').trim().toUpperCase() === 'GROWTH'
    && String(agent ?? '').trim().toLowerCase() === 'buck';
}

export type StripTone = 'walking' | 'stopping' | 'halted' | 'armed' | 'off' | 'unread';

export interface BuckStrip {
  line: string;
  tone: StripTone;
}

/** The lane, preferring the backend's OWN joined label. Re-joining the four
 *  parts here would be a second copy of a decision `_live_from_round` already
 *  made, including how it writes a missing level. The parts are used only when
 *  no label came. */
function laneText(live: RunnerLive): string | null {
  if (live.laneLabel) return live.laneLabel.toUpperCase();
  const parts = [live.lane.state, live.lane.metro_region,
                 live.lane.suburb_cluster, live.lane.sector]
    .filter((p): p is string => typeof p === 'string' && p.trim().length > 0);
  return parts.length ? parts.join(' · ').toUpperCase() : null;
}

/**
 * HOW LONG THE RUNNER WILL GO ON REFUSING TO START HIM, or null.
 *
 * TWO SENTENCES ON THIS SURFACE WERE FALSE FOR WANT OF THIS (owner ruling
 * 2026-09-01). The strip said "ARMED · NOT WALKING YET — A CHECK EVERY 60s
 * STARTS HIM" and the dot said "ON — NOTHING DUE". The check does fire every
 * 60s; `start()` then DECLINES it (growth_runner.py:583) for up to fifteen
 * minutes, so the sentence promised the opposite of what happens — with 22
 * companies due.
 *
 * NEVER PRINTS A FIGURE IT DID NOT READ. An unparseable stamp, or one already
 * in the past, returns null rather than a NaN or a negative minute — the same
 * discipline `ownCount` applies on the wire.
 */
export function coolingText(untilIso: string | null, now: number): string | null {
  if (!untilIso) return null;
  const until = Date.parse(untilIso);
  if (!Number.isFinite(until)) return null;
  const ms = until - now;
  if (ms <= 0) return null;
  const mins = Math.round(ms / 60000);
  // UNDER A MINUTE IS SAID AS UNDER A MINUTE. Rounding 20 seconds to "0 MIN"
  // would read as "not cooling", which is the fact this line exists to deny.
  if (mins < 1) return 'NOT RESTARTING FOR UNDER A MINUTE';
  return `NOT RESTARTING FOR ~${mins} MIN`;
}

/**
 * WHAT THE LAST FINISHED WALK DID — the half of the answer `live` cannot give.
 *
 * OWNER RULING 2026-08-31: "BUCK IDLE AFTER CLEARING EVERYTHING MUST NOT LOOK
 * IDENTICAL TO BUCK IDLE AT BOOT." `_last` is null in a freshly booted process
 * and populated the moment a walk ends, so its PRESENCE is the discriminator —
 * and its absence is stated rather than dressed up as a quiet nothing.
 */
function lastText(last: RunnerLast | null): string | null {
  if (!last) return null;
  const bits: string[] = [];
  if (last.rounds !== null) {
    bits.push(last.rounds === 1 ? 'LAST WALK BANKED 1 ROUND'
      : `LAST WALK BANKED ${last.rounds} ROUNDS`);
  } else {
    bits.push('LAST WALK ENDED');
  }
  // WHO ENDED IT IS NOT THE SAME FACT AS WHY. The owner turning him off and the
  // walk running out of lane both leave him idle, and they are not the same
  // thing to be looking at.
  if (last.stoppedByOwner) bits.push('YOU STOPPED HIM');
  else if (last.stopped) bits.push(last.stopped.toUpperCase());
  return bits.join(' · ');
}

/**
 * The strip. NEVER throws, and never shows a figure it did not read.
 *
 * ── `live` DESCRIBES A ROUND THAT HAS FINISHED, NOT ONE IN FLIGHT ──────────
 * THIS WAS WRONG IN THE FIRST BUILD AND THE OWNER READ IT AS WRONG. The runner
 * sets `_live` inside `on_round`, which fires AFTER a round banks — so
 * `live.round` is the last round COMPLETED and the round actually running is
 * the one after it, which nothing reports. Rendering "ROUND 3 OF 5" made a
 * banked round look like a position, so the one moment the owner asked for —
 * something finished — was the one thing it never said.
 *
 * AND THAT MOMENT IS THE CHEAPEST TIME TO STOP HIM. Flipping off mid-round
 * means waiting for it to bank; a round having JUST banked is the gap between
 * rounds, so the strip says so where it is true.
 */
/** Armed, not cooling: the runner's next check starts him (owner's words). */
export const ARMED_STARTING_LINE = 'ARMED — STARTING SHORTLY';

export function buckStrip(status: RunnerStatus | null, now: number = Date.now()): BuckStrip {
  if (!status || !status.reachable || status.state === 'unknown') {
    return {
      tone: 'unread',
      line: 'RUNNER STATE UNREAD — NOT READ, NOT OFF.',
    };
  }

  // ── STOPPING — THE STATE THE OWNER WENT TO SSH TO FIND ──────────────────
  // FIRST, and it takes no `live` detail: the owner named this sentence and it
  // is the log's own, which is where he had to read it. A round position beside
  // it would be answering a question he did not ask at the moment he is waiting
  // to be told one thing.
  if (status.state === 'stopping') {
    return { tone: 'stopping', line: STOPPING_LINE };
  }

  // ── HALTED — AN OUTAGE, SAID AS WHAT IS DOWN (owner ruling 2026-09-14) ───
  // The runner's alert in full: "the contact lane did not answer for 10 of 10
  // companies". Never "no progress" — that is not actionable. If a build sends
  // the word without the alert, the last walk's own stop reason is the same
  // sentence, and failing both it says HALTED and nothing it was not told.
  if (status.state === 'halted') {
    const said = status.halt?.alert || status.last?.stopped;
    return { tone: 'halted', line: said ? `HALTED · ${said.toUpperCase()}` : 'HALTED.' };
  }

  const last = lastText(status.last);

  if (status.state === 'off') {
    return {
      tone: 'off',
      // IDLE-AFTER-A-WALK vs IDLE-AT-BOOT, told apart by `last`.
      line: last ? `OFF · ${last}` : 'OFF · NOTHING HAS RUN IN THIS BUILD.',
    };
  }

  if (status.state === 'armed_not_walking') {
    // ── HE STOPPED HIMSELF, AND THE COOLDOWN IS THE ONLY THING THAT SAYS SO ──
    // `_cooldown_until` is set in `_walk`'s finally ONLY when the owner did not
    // stop him (growth_runner.py:527-531), so its presence IS the anti-spin
    // rule having fired: "a full round moved nothing — repeating them is a
    // spin, not work" (growth_harness.py:1180). Armed, not walking, and not
    // about to be. The owner spent twenty minutes believing he was working.
    const cooling = coolingText(status.coolingUntil, now);
    if (cooling) {
      const bits = ['ON · HE STOPPED HIMSELF'];
      // HIS REASON, IN FULL AND IN HIS OWN WORDS. Not shortened — the owner had
      // to read this exact sentence in `journalctl`, so a trimmed version here
      // sends him straight back to the terminal it exists to replace.
      if (status.last?.stopped) bits.push(status.last.stopped.toUpperCase());
      bits.push(cooling);
      return { tone: 'armed', line: bits.join(' · ') };
    }
    // ⭐ ARMED AND WAITING SAYS SO (owner ruling 2026-09-29). Reaching here means
    // no cooldown is running, so the runner's 60s check WILL start him — after a
    // restart, as soon as the old walk's claim expires. This line used to append
    // the last walk's stop reason, and after a restart that reason is "Stopped —
    // restart. He waits for the owner's press." — read on screen AFTER the owner
    // had pressed. The last walk's reason belongs to "off" and to the cooldown
    // above; here the present fact is the whole line.
    return { tone: 'armed', line: ARMED_STARTING_LINE };
  }

  // ── WALKING ─────────────────────────────────────────────────────────────
  // THE BUILD THAT PUBLISHES NO DETAIL IS ITS OWN SENTENCE. `liveKnown` false
  // means the route carried no `live` key at all — a server booted from an
  // older tree. Saying "walking" with a blank lane would read as a walk with
  // nothing in it.
  if (!status.liveKnown) {
    return { tone: 'walking', line: 'WALKING · THIS BUILD PUBLISHES NO ROUND DETAIL.' };
  }
  if (!status.live) {
    return { tone: 'walking', line: 'WALKING · NO ROUND BANKED YET — THE FIRST IS IN FLIGHT.' };
  }

  const L = status.live;
  const bits: string[] = [];

  // ── THE CYCLE (owner ruling 2026-09-28) — replaces "ROUND N OF 5" and
  // "LANE COMPLETE". State · trade, new this trade of ten, new this state of
  // fifty — each figure only when the producer sent it.
  const C = L.cycle;
  if (C) {
    const where = [C.state, C.trade].filter(Boolean).join(' · ').toUpperCase();
    if (where) bits.push(where);
    if (C.newThisTrade !== null) {
      bits.push(C.perTrade !== null ? `${C.newThisTrade} OF ${C.perTrade} NEW` : `${C.newThisTrade} NEW`);
    }
    if (C.newThisState !== null) {
      bits.push(C.stateQuota !== null
        ? `STATE ${C.newThisState} OF ${C.stateQuota}` : `STATE ${C.newThisState}`);
    }
    bits.push('RESEARCHING');
    return { tone: 'walking', line: bits.join(' · ') };
  }

  // ROUND N OF 5 COMPLETE — the completion, said as a completion.
  if (L.round !== null) {
    bits.push(L.roundsTarget !== null
      ? `ROUND ${L.round} OF ${L.roundsTarget} COMPLETE`
      : `ROUND ${L.round} COMPLETE`);
  } else {
    bits.push('WALKING');
  }

  // ZERO IS A REAL ANSWER and must survive — "0 DONE" is honest. Only `null`
  // (the completeness read itself failed) drops the fact rather than printing a
  // number nobody measured.
  if (L.companiesComplete !== null) {
    bits.push(L.companiesInRound !== null
      ? `${L.companiesComplete} OF ${L.companiesInRound} DONE`
      : `${L.companiesComplete} DONE`);
  }

  const lane = laneText(L);
  if (lane) bits.push(lane);

  // THE LANE IS FINISHED WHEN THE LAST ROUND OF IT HAS BANKED. Said only when
  // both numbers are known — a lane claim on a missing figure would be the
  // invented-reading fault in a new place.
  const laneDone = L.round !== null && L.roundsTarget !== null && L.round >= L.roundsTarget;
  if (laneDone) bits.push('LANE COMPLETE · NEXT LANE STARTING');
  else bits.push('NEXT ROUND RUNNING');

  return { tone: 'walking', line: bits.join(' · ') };
}

// ── `buckLoopDetail` WAS HERE AND IS DELETED (owner ruling 2026-08-31) ──────
// It returned the full paragraph on what arming Buck means — correct, ruled on
// 2026-08-28, and rendered into a navigation rail as eight lines of prose. "A
// CONTROL EXPLAINS ITSELF IN A FEW WORDS OR NOT AT ALL." Its only purpose was
// to produce screen text, so shortening it would have left a function whose
// name promised a paragraph; it is gone instead.
//
// WHAT IT SAID SURVIVES IN TWO PLACES, WHICH IS WHERE IT BELONGED:
//   • the rule — "he finishes the round in flight and is never killed
//     mid-company" — is `runControl.STOP_IS_NOT_INSTANT`, asserted by R10 and
//     convicted by mutation 17;
//   • the mechanism — the round-boundary consent re-read, and why there is no
//     kill call — is the header of THIS file, above.
